import React, { useRef, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IonRefresher,
  IonRefresherContent,
  IonButton,
  RefresherEventDetail,
} from '@ionic/react';
import {
  calendarOutline,
  flameOutline,
  newspaperOutline,
} from 'ionicons/icons';
import { useNavigate } from 'react-router-dom';
import ProCalendarCard from '../../components/pro/ProCalendarCard';
import ProBillsCard from '../../components/pro/ProBillsCard';
import ProNewsCard from '../../components/pro/ProNewsCard';
import ProQuickActions from '../../components/pro/ProQuickActions';
import ProExternalLinkSheet from '../../components/pro/bottom-sheet/ProExternalLinkSheet';
import ProSectionHeader, {
  PRO_SECTION_HEADER_BUTTON_PROPS,
} from '../../components/pro/ProSectionHeader';
import { Bill } from '../../types/bill';

const HOUSE_SCHEDULE_URL =
  'https://s3.us-west-2.amazonaws.com/accessible-docs.coleg.gov/house_calendar.html';
const SENATE_SCHEDULE_URL =
  'https://s3.us-west-2.amazonaws.com/accessible-docs.coleg.gov/senate_calendar.html';

const ProHomePage: React.FC = () => {
  const { t } = useTranslation('pro');
  const navigate = useNavigate();

  // Shared external-link confirmation sheet for House/Senate schedule links
  // on the calendar section header.
  const [linkSheet, setLinkSheet] = useState<{ open: boolean; title: string; url: string }>({
    open: false,
    title: '',
    url: '',
  });
  const openExternalLink = (title: string, url: string) => {
    setLinkSheet({ open: true, title, url });
  };
  const closeExternalLink = () => {
    setLinkSheet((prev) => ({ ...prev, open: false }));
  };

  const handleBillClick = useCallback(
    (bill: Bill) => {
      navigate(`/pro/bills/${bill.billNumber}`);
    },
    [navigate],
  );

  const calendarRefetchRef = useRef<(() => Promise<any>) | null>(null);
  const billsRefetchRef = useRef<(() => Promise<any>) | null>(null);
  const newsRefetchRef = useRef<(() => Promise<any>) | null>(null);

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    await Promise.all([
      calendarRefetchRef.current?.(),
      billsRefetchRef.current?.(),
      newsRefetchRef.current?.(),
    ]);
    event.detail.complete();
  };

  return (
    <>
      <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
        <IonRefresherContent />
      </IonRefresher>

      <ProQuickActions />

      <ProSectionHeader
        title={t('home.yourCalendar')}
        icon={calendarOutline}
        right={
          <>
            <IonButton
              {...PRO_SECTION_HEADER_BUTTON_PROPS}
              onClick={() => openExternalLink('House Schedule', HOUSE_SCHEDULE_URL)}
            >
              House
            </IonButton>
            <IonButton
              {...PRO_SECTION_HEADER_BUTTON_PROPS}
              onClick={() => openExternalLink('Senate Schedule', SENATE_SCHEDULE_URL)}
            >
              Senate
            </IonButton>
          </>
        }
      />
      <div className="px-2">
        <ProCalendarCard queryKey="pro-calendar" refetchRef={calendarRefetchRef} />
      </div>

      <ProSectionHeader title={t('home.hotBills')} icon={flameOutline} iconColor="danger" />
      <div className="px-2">
        <ProBillsCard
          queryKey="pro-bills-status"
          refetchRef={billsRefetchRef}
          onBillClick={handleBillClick}
        />
      </div>

      <ProSectionHeader title={t('home.latestNews')} icon={newspaperOutline} />
      <div className="px-2 pb-6">
        <ProNewsCard queryKey="pro-rss-news" refetchRef={newsRefetchRef} />
      </div>

      <ProExternalLinkSheet
        isOpen={linkSheet.open}
        onDismiss={closeExternalLink}
        title={linkSheet.title}
        url={linkSheet.url}
      />
    </>
  );
};

export default ProHomePage;
